import { Link } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';

interface PlanFeature {
  ko: string;
  en: string;
}

interface PlanCard {
  id: 'free' | 'pro' | 'business';
  name: string;
  eyebrow: { ko: string; en: string };
  price: { ko: string; en: string };
  cadence?: { ko: string; en: string };
  description: { ko: string; en: string };
  features: PlanFeature[];
  featured?: boolean;
}

const PLANS: PlanCard[] = [
  {
    id: 'free',
    name: 'Free',
    eyebrow: { ko: '개인용', en: 'For individuals' },
    price: { ko: '₩0', en: '₩0' },
    cadence: { ko: '계속 무료', en: 'Free forever' },
    description: {
      ko: '제안서 공유와 열람 분석을 가볍게 시작하세요.',
      en: 'Start sharing documents and learning what readers engage with.',
    },
    features: [
      {
        ko: 'Smartlake 기본 추출·해시 중복 제거',
        en: 'Smartlake baseline extraction and hash deduplication',
      },
      {
        ko: '고급 디스크립터는 CLI로 로컬 처리',
        en: 'Advanced descriptors processed locally with the CLI',
      },
      { ko: '문서 최대 100개', en: 'Up to 100 documents' },
      { ko: '저장공간 1GB', en: '1 GB storage' },
      { ko: '문서당 대상별 링크 5개', en: '5 audience links per document' },
      { ko: '링크 유효기간 최대 7일', en: 'Links active for up to 7 days' },
      { ko: '워크스페이스 주소 1개', en: '1 workspace address' },
      { ko: '열람·방문자·읽기 깊이 분석', en: 'Opens, visitors, and reading-depth analytics' },
      { ko: '뷰어에 "powered by CloudBTL" 표시', en: 'Viewer shows a “powered by CloudBTL” mark' },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    eyebrow: { ko: '가장 인기 · 개인·소규모', en: 'Most popular' },
    price: { ko: '$9.9', en: '$9.9' },
    cadence: { ko: '/ 월', en: '/ month' },
    description: {
      ko: 'Free로 맛봤다면 Pro. 내 브랜드로, 만료 없이 보냅니다.',
      en: 'Outgrown Free? Pro sends under your own brand, with links that never expire.',
    },
    features: [
      {
        ko: 'Smartlake 기본 추출·해시 중복 제거',
        en: 'Smartlake baseline extraction and hash deduplication',
      },
      {
        ko: '고급 디스크립터는 CLI로 로컬 처리',
        en: 'Advanced descriptors processed locally with the CLI',
      },
      { ko: 'CloudBTL 표시 없는 화이트라벨', en: 'White-label — no CloudBTL badge' },
      { ko: '링크 유효기간 무제한', en: 'Links never expire' },
      { ko: '워크스페이스 주소 3개', en: '3 workspace addresses' },
      { ko: '문서 최대 2,000개', en: 'Up to 2,000 documents' },
      { ko: '저장공간 20GB', en: '20 GB storage' },
      { ko: '문서당 대상별 링크 100개', en: '100 audience links per document' },
    ],
    featured: true,
  },
  {
    id: 'business',
    name: 'Business',
    eyebrow: { ko: '조직용', en: 'For organizations' },
    price: { ko: '$29.9', en: '$29.9' },
    cadence: { ko: '/ 월', en: '/ month' },
    description: {
      ko: '장기 보관, 대규모 공유, 브랜드 경험이 필요한 조직을 위한 플랜입니다.',
      en: 'For organizations that need long-term access, scale, and a branded experience.',
    },
    features: [
      { ko: '관리형 서버 배치 처리 베타', en: 'Managed server batch processing beta' },
      { ko: '문서 최대 50,000개', en: 'Up to 50,000 documents' },
      { ko: '저장공간 500GB', en: '500 GB storage' },
      { ko: '문서당 대상별 링크 10,000개', en: '10,000 audience links per document' },
      { ko: '링크 유효기간 제한 없음', en: 'No link expiration limit' },
      { ko: '워크스페이스 주소 최대 50개', en: 'Up to 50 workspace addresses' },
      { ko: 'CloudBTL 표시 없는 화이트라벨', en: 'White-label viewer without CloudBTL branding' },
      { ko: '엔터프라이즈 SSO 발급자 관리', en: 'Enterprise SSO issuer management' },
    ],
  },
];

const COMPARISON = [
  {
    label: { ko: '디스크립터 처리', en: 'Descriptor processing' },
    free: { ko: '기본 + 로컬 CLI', en: 'Baseline + local CLI' },
    pro: { ko: '기본 + 로컬 CLI', en: 'Baseline + local CLI' },
    business: { ko: '관리형 배치 베타', en: 'Managed batch beta' },
  },
  {
    label: { ko: '문서', en: 'Documents' },
    free: '100',
    pro: '2,000',
    business: '50,000',
  },
  {
    label: { ko: '저장공간', en: 'Storage' },
    free: '1 GB',
    pro: '20 GB',
    business: '500 GB',
  },
  {
    label: { ko: '대상별 링크 / 문서', en: 'Audience links / document' },
    free: '5',
    pro: '100',
    business: '10,000',
  },
  {
    label: { ko: '링크 유효기간', en: 'Link lifetime' },
    free: { ko: '7일', en: '7 days' },
    pro: { ko: '무제한', en: 'Unlimited' },
    business: { ko: '무제한', en: 'Unlimited' },
  },
  {
    label: { ko: '업로드 / 시간', en: 'Uploads / hour' },
    free: '60',
    pro: '600',
    business: '6,000',
  },
  {
    label: { ko: '화이트라벨', en: 'White label' },
    free: '—',
    pro: { ko: '포함', en: 'Included' },
    business: { ko: '포함', en: 'Included' },
  },
  {
    label: { ko: '엔터프라이즈 SSO', en: 'Enterprise SSO' },
    free: '—',
    pro: '—',
    business: { ko: '포함', en: 'Included' },
  },
] as const;

const SALES_EMAIL =
  'mailto:hi@cloudbtl.com?subject=CloudBTL%20Business%20%EB%8F%84%EC%9E%85%20%EB%AC%B8%EC%9D%98';
export function PlansPage(): JSX.Element {
  const t = useT();
  useDocumentMeta({
    title: t({ ko: '플랜', en: 'Plans' }),
    description: t({
      ko: 'CloudBTL Free, 월 $9.9 Pro, 월 $29.9 Business 플랜의 문서 수, 저장공간, 공유 링크, 워크스페이스 기능을 비교하세요.',
      en: 'Compare CloudBTL Free, $9.9/month Pro, and $29.9/month Business plans for document limits, storage, audience links, and workspace features.',
    }),
    canonical: '/plans',
    image: '/og-plans.png',
  });

  return (
    <Shell>
      <article className="plans-page">
        <header className="plans-hero">
          <span className="plans-kicker">CloudBTL Plans</span>
          <h1>
            {t({
              ko: '시작은 가볍게, 필요할 때 확장하세요.',
              en: 'Start simple. Scale when you need to.',
            })}
          </h1>
          <p>
            {t({
              ko: '모든 플랜에서 파일을 Smartlake에 보관하고 기본 디스크립터를 만들며, 추적 링크로 공유할 수 있습니다.',
              en: 'Every plan can keep files in Smartlake, create baseline descriptors, and share them as tracked links.',
            })}
          </p>
        </header>

        <section
          className="plan-grid"
          aria-label={t({ ko: 'CloudBTL 플랜', en: 'CloudBTL plans' })}
        >
          {PLANS.map((plan) => (
            <article
              className={`plan-card${plan.featured ? ' plan-card--featured' : ''}`}
              data-testid={`plan-${plan.id}`}
              key={plan.id}
            >
              {plan.featured ? (
                <span className="plan-badge">
                  {t({ ko: '가장 균형 잡힌 선택', en: 'Best balance' })}
                </span>
              ) : null}
              <p className="plan-eyebrow">{t(plan.eyebrow)}</p>
              <h2>{plan.name}</h2>
              <div className="plan-price">
                <strong>{t(plan.price)}</strong>
                {plan.cadence ? <span>{t(plan.cadence)}</span> : null}
              </div>
              <p className="plan-description">{t(plan.description)}</p>
              {plan.id === 'free' ? (
                <div className="plan-actions">
                  <Link to="/lake" className="plan-action plan-action--primary">
                    {t({ ko: '무료로 시작', en: 'Start for free' })}
                  </Link>
                </div>
              ) : (
                <div className="plan-actions">
                  <Link
                    to={`/workspace?trial=${plan.id}`}
                    className={`plan-action${plan.featured ? ' plan-action--primary' : ''}`}
                  >
                    {t({ ko: '30일 무료 체험하기', en: 'Start 30-day free trial' })}
                  </Link>
                  <Link to={`/workspace?subscribe=${plan.id}`} className="plan-action">
                    {t({ ko: 'PayPal로 바로 구독', en: 'Subscribe with PayPal' })}
                  </Link>
                </div>
              )}
              <ul className="plan-features">
                {plan.features.map((feature) => (
                  <li key={feature.en}>
                    <span aria-hidden="true">✓</span>
                    {t(feature)}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="plan-comparison" aria-labelledby="plan-comparison-title">
          <div className="plan-section-heading">
            <p className="plans-kicker">{t({ ko: '한눈에 비교', en: 'Compare' })}</p>
            <h2 id="plan-comparison-title">
              {t({ ko: '팀의 문서 운영량에 맞는 플랜', en: 'A plan for your document workload' })}
            </h2>
          </div>
          <div className="plan-table-wrap">
            <table className="plan-table">
              <thead>
                <tr>
                  <th scope="col">{t({ ko: '기능', en: 'Feature' })}</th>
                  <th scope="col">Free</th>
                  <th scope="col">Pro</th>
                  <th scope="col">Business</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.label.en}>
                    <th scope="row">{t(row.label)}</th>
                    <td>{typeof row.free === 'string' ? row.free : t(row.free)}</td>
                    <td>{typeof row.pro === 'string' ? row.pro : t(row.pro)}</td>
                    <td>{typeof row.business === 'string' ? row.business : t(row.business)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="plan-callout" aria-labelledby="plan-callout-title">
          <div>
            <p className="plans-kicker">{t({ ko: '결제수단 없이', en: 'No payment method' })}</p>
            <h2 id="plan-callout-title">
              {t({
                ko: 'Pro와 Business를 30일간 체험하세요.',
                en: 'Try Pro or Business for 30 days.',
              })}
            </h2>
            <p>
              {t({
                ko: '카드 등록 없이 워크스페이스당 한 번 시작할 수 있습니다. 30일 뒤 자동 결제 없이 Free 플랜으로 돌아갑니다.',
                en: 'Start once per workspace without a card. After 30 days, it returns to Free with no automatic charge.',
              })}
            </p>
          </div>
          <div className="plan-callout-actions">
            <Link to="/lake" className="cta-primary">
              {t({ ko: '문서 올리기', en: 'Upload a document' })}
            </Link>
            <a href={SALES_EMAIL} className="cta-secondary">
              {t({ ko: '팀에 문의하기', en: 'Talk to the team' })}
            </a>
          </div>
        </section>

        <p className="plan-footnote">
          {t({
            ko: '표시된 한도는 현재 서비스 정책 기준이며, 유료 플랜 가격은 USD 기준 월 요금입니다. Business의 관리형 배치 처리는 현재 베타 온보딩으로 개통합니다. 무료 체험은 PayPal 구독과 별개이며 체험 종료 후 자동 청구되지 않습니다.',
            en: 'Limits reflect the current service policy and paid prices are monthly in USD. Managed batch processing on Business is currently enabled through beta onboarding. Trials are separate from PayPal subscriptions and never auto-charge.',
          })}
        </p>
      </article>
    </Shell>
  );
}
